import { useEffect, useState, useMemo } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { userHashAtom } from '@!/atoms/userInfoAtom'
import { buildUrlString } from '@!/Util'
import { PrimaryButton } from '@!/components/Button'
import { CircularProgressModal, ConfirmPopupModal, YesNoPopupModal } from '@!/components/Modal'
import { getEndDate } from '@!/User/Bookshelf/util/Check'
import { useReadBook, useAppCookies } from '@!/Hooks'
import { bookDetailsApi } from '@!/Api/storeBook'

export const ReadFreeBookButton = ({ content }) => {
  const [userHash] = useAtom(userHashAtom)

  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [warningModalOpen, setWarningModalOpen] = useState(false)
  const bookDetailsMutation = useMutation({
    mutationFn: () => bookDetailsApi(content.content_id),
    onError: (error) => {
      if (error && error.data) {
        setApiError(error.data)
      } else {
        setApiError('server error')
      }
    },
  })

  const endDate = useMemo(() => getEndDate(content), [content])
  const confirmPopupText = useMemo(() => `「${content.content_name}」は${endDate}まで閲覧できます。`, [content, endDate])

  const [readFreeContentMutation, redirectUrl, apiError, setApiError] = useReadBook('free')

  useEffect(() => {
    if (!redirectUrl) return

    window.location.href = redirectUrl
  }, [redirectUrl])

  const execReadBook = async () => {
    setConfirmModalOpen(false)
    readFreeContentMutation.mutate({ contentId: content.content_id })
  }

  const handleReadFreeContent = (event) => {
    event.preventDefault()

    if (content.adult_flag >= 2) {
      setWarningModalOpen(true)
    } else {
      executeAction()
    }
  }

  const handleCancelDialog = () => {
    setWarningModalOpen(false)
  }

  const handleWarningSubmit = () => {
    setWarningModalOpen(false)
    executeAction()
  }

  const executeAction = () => {
    bookDetailsMutation.mutate()
  }

  useEffect(() => {
    if (!bookDetailsMutation.isSuccess) return

    if (!bookDetailsMutation.data || bookDetailsMutation.data.length === 0 || bookDetailsMutation.data[0].price !== 0) {
      // 価格が0円ではない場合の処理
      setApiError(true)
      return
    }

    // ログイン済みの場合は0円決済して本棚に登録させる
    if (userHash) {
      window.top.location.href = buildUrlString('/store/purchase', { book_id: content.content_id })
      return
    }

    if (endDate) {
      setConfirmModalOpen(true)
    } else {
      execReadBook()
    }
  }, [bookDetailsMutation.isSuccess])

  const handleApiError = () => {
    console.log(apiError)
    setApiError(null)
  }

  const { openViewerCookie, removeOpenViewerCookie } = useAppCookies()

  useEffect(() => {
    if (openViewerCookie === 'free') {
      removeOpenViewerCookie()
      // 即時ビューワー起動で詳細画面の履歴がスキップされるのを防ぐため、pushStateで履歴を念のため追加
      window.history.pushState(null, '', `/store/books/${content.content_id}`)
      execReadBook()
    }
  }, [openViewerCookie])

  return (
    <>
      <PrimaryButton width="100%" height="37px" onClick={(event) => handleReadFreeContent(event)}>
        無料で読む
      </PrimaryButton>
      <YesNoPopupModal
        title="この作品には性的・暴力的な表現が一部含まれている可能性があります。ご了承の上閲覧しますか?"
        handleCancel={() => handleCancelDialog()}
        handleSubmit={() => handleWarningSubmit()}
        cancelTitle="キャンセル"
        submitTitle="はい"
        isOpen={warningModalOpen}
      />
      <ConfirmPopupModal handlePopup={() => execReadBook()} text={confirmPopupText} isOpen={confirmModalOpen} />
      <ConfirmPopupModal text="エラーが発生しました。" handlePopup={() => handleApiError()} isOpen={!!apiError} />
      <CircularProgressModal isOpen={readFreeContentMutation.isPending} />
    </>
  )
}
